import { useChains } from "@opensea/app-kit"
import type { ChainIdentifier } from "@opensea/graphql"
import {
  Avatar,
  Item,
  ItemContent,
  ItemSide,
  ItemTitle,
} from "@opensea/next-ui-kit"
import { FlexCenter, FlexColumn, TextBody, Tooltip, tv } from "@opensea/ui-kit"
import { Sensors } from "@opensea/ui-kit/icons"
import { useTranslations } from "next-intl"

type ChainStatus = "Active" | "Degraded" | "Beta" | "Coming Soon"
const statusBadge = tv({
  base: "rounded px-1.5 py-0.5",
  variants: {
    status: {
      Active: "bg-success-1-transparent-1 text-success-1",
      Degraded: "bg-error-1-transparent-1 text-error-1",
      Beta: "bg-caution-transparent-1 text-caution",
      "Coming Soon": "bg-bg-additional-1 text-text-secondary",
    },
  },
  defaultVariants: {
    status: "Active",
  },
})

const betaChains: ChainIdentifier[] = [] as const

export function NetworksHoverable() {
  const t = useTranslations("NetworksHoverable")

  return (
    <Tooltip content={<NetworkContent />}>
      <FlexCenter className="gap-1" color="text-secondary">
        <Sensors size={16} />
        <TextBody size="xs">{t("networks")}</TextBody>
      </FlexCenter>
    </Tooltip>
  )
}

function NetworkContent() {
  const { evmChains, getChainName } = useChains()
  const t = useTranslations("NetworksHoverable")

  return (
    <FlexColumn className="w-[280px] font-sans">
      <div className="px-2 py-1.5">{t("allNetworks")}</div>
      {evmChains.map(({ identifier }) => (
        <NetworkItem
          identifier={identifier}
          key={identifier}
          name={getChainName(identifier)}
          status="Active"
        />
      ))}
      {evmChains
        .filter(({ identifier }) => betaChains.includes(identifier))
        .map(({ identifier }) => (
          <Tooltip content={t("tooltip")} key={identifier}>
            <span>
              <NetworkItem
                identifier={identifier}
                key={identifier}
                name={getChainName(identifier)}
                status="Beta"
              />
            </span>
          </Tooltip>
        ))}
    </FlexColumn>
  )
}

function NetworkItem({
  identifier,
  status,
  name,
}: {
  identifier: ChainIdentifier
  status: ChainStatus
  name: string
}) {
  const { getLogo } = useChains()
  const logo = getLogo(identifier)
  return (
    <Item className="px-2 py-1.5" key={identifier} variant="unstyled">
      <Avatar {...logo} size={20} />
      <ItemContent>
        <ItemTitle>{name}</ItemTitle>
      </ItemContent>
      <ItemSide>
        <ItemTitle className={statusBadge({ status })}>{status}</ItemTitle>
      </ItemSide>
    </Item>
  )
}
